import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { SuccessSwal, ErrorSwal } from '@shared/helpers/swal';
import { getServiciosSpread } from '@shared/helpers/api/mantenedores/generales';

const EmpresaModal = ({ user, show, onHide, empresaId, permisos, fetchEmpresa, empresa, onUpdate, onDelete, loading }) => {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [savingChanges, setSavingChanges] = useState(false);
  const [showAddPriceModal, setShowAddPriceModal] = useState(false);
  const [serviciosSpread, setServiciosSpread] = useState([]);
  const [loadingServicios, setLoadingServicios] = useState(false);
  const [savingPrice, setSavingPrice] = useState(false);
  const [newPrice, setNewPrice] = useState({
    servicio_id: '',
    precio: ''
  });

  useEffect(() => {
    if (empresaId) {
      fetchEmpresa(empresaId);
    }
    setEditMode(false);
  }, [empresaId, fetchEmpresa]);

  useEffect(() => {
    if (empresa) {
      setForm({ ...empresa });
    }
  }, [empresa]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePrecioChange = (servicioId, nuevoPrecio) => {
    setForm(prev => ({
      ...prev,
      precio_empresa: prev.precio_empresa?.map(item => 
        item.servicio_spread.id === servicioId 
          ? { ...item, precio: parseFloat(nuevoPrecio) || 0 }
          : item
      ) || []
    }));
  };

  // Funciones para el modal de agregar precio
  const handleOpenAddPriceModal = async () => {
    // Abrir el modal inmediatamente
    setNewPrice({ servicio_id: '', precio: '' });
    setShowAddPriceModal(true);
    
    // Luego cargar los servicios
    setLoadingServicios(true);
    try {
      // Obtener servicios spread desde la API
      const response = await getServiciosSpread(user);
      if (response.success) {
        setServiciosSpread(response.data || []);
      } else {
        console.error('Error al obtener servicios spread:', response.error);
        ErrorSwal('Error', 'No se pudieron cargar los servicios');
        setShowAddPriceModal(false); // Cerrar el modal si hay error
        return;
      }
    } catch (error) {
      console.error('Error al cargar servicios:', error);
      ErrorSwal('Error', 'Error al cargar los servicios');
      setShowAddPriceModal(false); // Cerrar el modal si hay error
      return;
    } finally {
      setLoadingServicios(false);
    }
  };

  const handleCloseAddPriceModal = () => {
    setShowAddPriceModal(false);
    setNewPrice({ servicio_id: '', precio: '' });
  };

  const handleNewPriceChange = (e) => {
    const { name, value } = e.target;
    setNewPrice(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveNewPrice = async () => {
    if (!newPrice.servicio_id || !newPrice.precio) {
      ErrorSwal('Error', 'Debe seleccionar un servicio e ingresar un precio');
      return;
    }

    // Verificar que el servicio no exista ya en los precios actuales
    const existingService = form.precio_empresa?.find(item => 
      item.servicio_spread.id === parseInt(newPrice.servicio_id)
    );

    if (existingService) {
      ErrorSwal('Error', 'Ya existe un precio para este servicio');
      return;
    }

    setSavingPrice(true);
    try {
      // Preparar datos para enviar
      const updateData = { ...form };
      
      // Agregar el nuevo precio a la estructura existente
      const currentPrecios = form.precio_empresa || [];
      const nuevoPrecio = {
        servicio_id: parseInt(newPrice.servicio_id),
        precio: parseFloat(newPrice.precio)
      };

      // Formatear precios existentes + nuevo precio
      updateData.precio_empresa = [
        ...currentPrecios.map(item => ({
          servicio_id: item.servicio_spread.id,
          precio: item.precio
        })),
        nuevoPrecio
      ];

      if (onUpdate) {
        const result = await onUpdate(empresaId, updateData);
        console.log('Resultado del update:', result);
        
        // Verificar si la respuesta es exitosa
        if (result && result.success === true) {
          SuccessSwal('Éxito', result?.message || 'Precio agregado correctamente');
          setShowAddPriceModal(false);
          setNewPrice({ servicio_id: '', precio: '' });
          // Recargar datos de la empresa
          fetchEmpresa(empresaId);
        } else {
          ErrorSwal('Error', result?.error || 'Error al agregar el precio');
        }
      }
    } catch (error) {
      console.error('Error al agregar precio:', error);
      ErrorSwal('Error', 'Error inesperado al agregar el precio');
    } finally {
      setSavingPrice(false);
    }
  };

  const handleEdit = () => setEditMode(true);
  const handleCancel = () => {
    setEditMode(false);
    setForm({ ...empresa });
  };
  const handleSave = async () => {
    if (onUpdate) {
      setSavingChanges(true);
      try {
        // Preparar los datos para el update
        const updateData = { ...form };
        
        // Si hay precios de empresa, formatear correctamente
        if (form.precio_empresa && Array.isArray(form.precio_empresa)) {
          updateData.precio_empresa = form.precio_empresa.map(item => ({
            servicio_id: item.servicio_spread.id,
            precio: item.precio
          }));
        }
        
        const result = await onUpdate(empresaId, updateData);
        console.log('Resultado del update principal:', result);
        
        // Verificar si la respuesta es exitosa
        if (result && result.success === true) {
          SuccessSwal('Éxito', result?.message || 'Empresa actualizada correctamente');
          setEditMode(false);
        } else {
          ErrorSwal('Error', result?.error || 'Error al actualizar la empresa');
        }
      } catch (error) {
        console.error('Error al guardar cambios:', error);
        ErrorSwal('Error', 'Error inesperado al guardar los cambios');
      } finally {
        setSavingChanges(false);
      }
    }
  };

  const handleDelete = () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: `¿Deseas deshabilitar la empresa "${empresa?.razonSocial || empresa?.sigla}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, deshabilitar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (onDelete) {
          const response = await onDelete(empresaId);
          if (response && response.success) {
            SuccessSwal('Empresa deshabilitada', response.message || 'La empresa ha sido deshabilitada correctamente');
            onHide(); // Cerrar el modal
          } else {
            ErrorSwal('Error', response?.error || 'Error al deshabilitar la empresa');
          }
        }
      }
    });
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Detalles de Empresa</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '180px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        ) : empresa ? (
          <Form>
            <div className="mb-4">
              <h5 className="mb-3">Información Empresa y Contacto Comercial</h5>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label>Sigla</Form.Label>
                    <Form.Control value={form.sigla || ''} disabled size="sm" />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Razón Social</Form.Label>
                    <Form.Control value={form.razonSocial || ''} disabled size="sm" />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Rut</Form.Label>
                    <Form.Control value={form.rut || ''} disabled size="sm" />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label>Tipo Documento</Form.Label>
                    <Form.Control value={form.tipo_documento?.documento || ''} disabled size="sm" />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Dirección</Form.Label>
                    <Form.Control
                      name="direccion"
                      value={form.direccion || ''}
                      onChange={handleChange}
                      disabled={!editMode}
                      size="sm"
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Precio Forzado</Form.Label>
                    <div style={{ display: 'flex', alignItems: 'center', height: '31px' }}>
                      <Form.Check 
                        name="is_forced" 
                        checked={form.is_forced === true} 
                        onChange={handleChange} 
                        label="Forzar precios" 
                        disabled={!editMode}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label>Contacto Comercial</Form.Label>
                    <Form.Control
                      name="contactoComercial"
                      value={form.contactoComercial || ''}
                      onChange={handleChange}
                      disabled={!editMode}
                      size="sm"
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Teléfono Comercial</Form.Label>
                    <Form.Control
                      name="telefonoComercial"
                      value={form.telefonoComercial || ''}
                      onChange={handleChange}
                      disabled={!editMode}
                      size="sm"
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Email Comercial</Form.Label>
                    <Form.Control
                      name="emailComercial"
                      value={form.emailComercial || ''}
                      onChange={handleChange}
                      disabled={!editMode}
                      size="sm"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
            <div>
              <h5 className="mb-3">Información del Jefe de Flota</h5>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label>Jefe Flota</Form.Label>
                    <Form.Control
                      name="jefeFlota"
                      value={form.jefeFlota || ''}
                      onChange={handleChange}
                      disabled={!editMode}
                      size="sm"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label>Teléfono Jefe Flota</Form.Label>
                    <Form.Control
                      name="telefonoJf"
                      value={form.telefonoJf || ''}
                      onChange={handleChange}
                      disabled={!editMode}
                      size="sm"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label>Email Jefe Flota</Form.Label>
                    <Form.Control
                      name="emailJf"
                      value={form.emailJf || ''}
                      onChange={handleChange}
                      disabled={!editMode}
                      size="sm"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
            
            {/* Sección de Precios Empresa - Solo si existen precios */}
            {form.precio_empresa && Array.isArray(form.precio_empresa) && form.precio_empresa.length > 0 && (
              <div className="mt-4">
                <h5 className="mb-3">Precios Empresa</h5>
                <Row>
                  {form.precio_empresa.map((precioItem, index) => (
                    <Col md={4} key={precioItem.id || index}>
                      <Form.Group className="mb-2">
                        <Form.Label>{precioItem.servicio_spread?.servicio || 'Servicio'}</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <Form.Control
                            type="number"
                            value={precioItem.precio || 0}
                            onChange={(e) => handlePrecioChange(precioItem.servicio_spread.id, e.target.value)}
                            disabled={!editMode}
                            size="sm"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          </Form>
        ) : (
          <div>Cargando datos...</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {(permisos.canDelete || permisos.canAdmin) && (
              <Button variant="danger" onClick={handleDelete}>
                Deshabilitar Empresa
              </Button>
            )}
          </div>
          <div>
            {(permisos.canEdit || permisos.canAdmin) && (
              <Button 
                variant="success" 
                onClick={handleOpenAddPriceModal} 
                className="me-2"
                disabled={loadingServicios}
              >
                {loadingServicios ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Cargando...
                  </>
                ) : (
                  'Agregar Precio'
                )}
              </Button>
            )}
            {permisos.canEdit || permisos.canAdmin ? (
              editMode ? (
                <>
                  <Button variant="secondary" onClick={handleCancel} className="me-2" disabled={savingChanges}>Cancelar</Button>
                  <Button variant="primary" onClick={handleSave} className="me-2" disabled={savingChanges}>
                    {savingChanges ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Guardando...
                      </>
                    ) : (
                      'Guardar'
                    )}
                  </Button>
                </>
              ) : (
                <Button variant="warning" onClick={handleEdit} className="me-2">Editar</Button>
              )
            ) : null}
            <Button variant="outline-dark" onClick={onHide}>Cerrar</Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>

    {/* Modal para agregar nuevo precio */}
    <Modal show={showAddPriceModal} onHide={handleCloseAddPriceModal} size="md" centered>
      <Modal.Header closeButton>
        <Modal.Title>Agregar Nuevo Precio</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingServicios ? (
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '150px' }}>
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="text-muted">Cargando servicios disponibles...</p>
          </div>
        ) : (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Servicio Spread</Form.Label>
              <Form.Select
                name="servicio_id"
                value={newPrice.servicio_id}
                onChange={handleNewPriceChange}
                required
              >
                <option value="">Seleccionar servicio</option>
                {serviciosSpread.map((servicio) => (
                  <option key={servicio.id} value={servicio.id}>
                    {servicio.servicio}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Precio</Form.Label>
              <div className="input-group">
                <span className="input-group-text">$</span>
                <Form.Control
                  type="number"
                  name="precio"
                  value={newPrice.precio}
                  onChange={handleNewPriceChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleCloseAddPriceModal}
          disabled={loadingServicios || savingPrice}
        >
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSaveNewPrice}
          disabled={loadingServicios || !newPrice.servicio_id || !newPrice.precio || savingPrice}
        >
          {savingPrice ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Guardando...
            </>
          ) : (
            'Guardar Precio'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

EmpresaModal.propTypes = {
  user: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  empresaId: PropTypes.number,
  permisos: PropTypes.object.isRequired,
  fetchEmpresa: PropTypes.func.isRequired,
  empresa: PropTypes.object,
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  loading: PropTypes.bool,
};

export default EmpresaModal;
